import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { DatePipe, NgClass } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';

import { AnnouncementsService } from '../services/announcements.service';
import { AnnouncementAttachment } from '../models/announcement.models';
import { LoggerService } from '../../../shared/services/logger.service';
import { SnackBarService } from '../../../shared/services/snack-service.service';
import { FileSizePipe } from '../../../shared/pipes/file-size.pipe';
import { FileTypePipe } from '../../../shared/pipes/file-type.pipe';
import { canViewAttachmentInBrowser, viewableContentType } from './attachment-view.util';

/**
 * Attachment list shared by the announcement details page and the blocking gate dialog.
 *
 * Replaces `app-file-display` for announcements because that component always offers
 * "View" - here it is offered only for passive formats (see `canViewAttachmentInBrowser`).
 * View and download failures are reported to the reader instead of failing silently.
 */
@Component({
  selector: 'ri-announcement-attachments',
  standalone: true,
  imports: [NgClass, DatePipe, MatButtonModule, MatIconModule, TranslatePipe, FileSizePipe, FileTypePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './announcement-attachments.component.html',
  styleUrls: ['./announcement-attachments.component.scss']
})
export class AnnouncementAttachmentsComponent {
  @Input() attachments: AnnouncementAttachment[] | null | undefined = [];

  constructor(
    private readonly announcementsService: AnnouncementsService,
    private readonly snackBar: SnackBarService,
    private readonly logger: LoggerService
  ) { }

  canView(attachment: AnnouncementAttachment): boolean {
    return canViewAttachmentInBrowser(attachment.contentType, attachment.fileName);
  }

  onDownload(attachment: AnnouncementAttachment): void {
    this.announcementsService.downloadAttachment(attachment.downloadUrl).subscribe({
      next: blob => this.saveBlob(blob, attachment.fileName),
      error: err => this.onFailure('Failed to download attachment', err)
    });
  }

  /**
   * Opens the attachment in a new tab. The tab is opened synchronously, inside the click
   * handler, so it carries the user-gesture flag: a blob URL only exists after an async
   * fetch and a window opened from that callback would be blocked as a popup. When the
   * tab could not be opened anyway, the file is downloaded instead.
   */
  onView(attachment: AnnouncementAttachment): void {
    const viewType = viewableContentType(attachment.contentType, attachment.fileName);
    if (!viewType) {
      this.onDownload(attachment);
      return;
    }

    const tab = window.open('', '_blank');
    this.announcementsService.downloadAttachment(attachment.downloadUrl).subscribe({
      next: blob => {
        if (tab) {
          // Re-type to the allowlisted type so the tab can never hold active content.
          const url = window.URL.createObjectURL(new Blob([blob], { type: viewType }));
          tab.location.href = url;
          tab.addEventListener('load', () => window.URL.revokeObjectURL(url), { once: true });
        } else {
          this.saveBlob(blob, attachment.fileName);
          this.snackBar.info('ANNOUNCEMENTS.DETAILS.ATTACHMENT_POPUP_BLOCKED', { translate: true });
        }
      },
      error: err => {
        tab?.close();
        this.onFailure('Failed to view attachment', err);
      }
    });
  }

  getFileIcon(contentType: string): string {
    if (contentType === 'application/pdf') return 'picture_as_pdf';
    if (contentType?.startsWith('image/')) return 'image';
    if (contentType?.includes('word')) return 'description';
    if (contentType?.includes('excel') || contentType?.includes('spreadsheet')) return 'table_chart';
    if (contentType?.includes('powerpoint') || contentType?.includes('presentation')) return 'slideshow';
    if (contentType?.includes('zip') || contentType?.includes('rar')) return 'folder_zip';
    if (contentType === 'text/plain') return 'description';
    return 'insert_drive_file';
  }

  getFileTypeClass(contentType: string): string {
    if (contentType === 'application/pdf') return 'type-pdf';
    if (contentType?.startsWith('image/')) return 'type-image';
    return 'type-default';
  }

  private saveBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  private onFailure(message: string, error: unknown): void {
    this.logger.errorWithContext('AnnouncementAttachmentsComponent', message, error);
    this.snackBar.error('ANNOUNCEMENTS.DETAILS.ATTACHMENT_ERROR', { translate: true });
  }
}
